import { useRef, useState } from 'react'
import { useMeeting } from '@videosdk.live/react-sdk'

export const useAudioRecorder = () => {
  const [audioChunks, setAudioChunks] = useState<Blob[]>([])
  const [file, setFile] = useState<File | null>()

  const mediaRecorder = useRef<MediaRecorder | null>(null)
  const stream = useRef<MediaStream | null>(null)

  const { participants } = useMeeting()
  const mimeType = 'audio/webm'

  const mixedAudioStream = useRef<MediaStream | null>(null)

  const initializeRoomRecorder = (meeting: any) => {
    mixedAudioStream.current = meeting.getAudioStream()

    const media = new MediaRecorder(mixedAudioStream.current, {
      mimeType: 'audio/webm',
    })

    mediaRecorder.current = media
    mediaRecorder.current.start()

    const chunks: Blob[] = []

    mediaRecorder.current.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunks.push(e.data)
      }
    }

    mediaRecorder.current.onstop = () => {
      const blob = new Blob(chunks, { type: 'audio/webm' })
      const finalFile = new File([blob], 'full-session.webm', {
        type: 'audio/webm',
      })
      setFile(finalFile)
    }

    mediaRecorder.current.pause()
  }

  const initializeAudioRecorder = (userId: string) => {
    const participant = participants.get(userId)

    if (!participant) throw new Error('User not found')

    const audioStream = [...participant.streams.values()].find((stream) => stream.kind === 'audio')

    if (!audioStream) throw new Error('Audio stream not set in useAudioRecorder')

    // set mediastream as stream ref
    stream.current = new MediaStream()
    // add audio stream track to stream
    stream.current.addTrack(audioStream.track)

    if (!stream) return

    // create media recorder
    const media = new MediaRecorder(stream.current, { mimeType: mimeType })

    // set media recorder as mediaRecorder ref
    mediaRecorder.current = media

    // start media recorder
    mediaRecorder.current.start()

    let localAudioChunks: Blob[] = []

    // add event listener to handle when data is available
    mediaRecorder.current.ondataavailable = (event: { data: Blob }) => {
      if (typeof event.data === 'undefined') return
      if (event.data.size === 0) return
      // if there is something in data, push it to localAudioChunks
      localAudioChunks.push(event.data)
      setAudioChunks([...localAudioChunks]) // Update state
    }

    // set localAudioChunks as audioChunks state
    setAudioChunks(localAudioChunks)

    // pause the recorder for now (resume later by user interaction)
    mediaRecorder.current?.pause()
  }

  const pauseRecording = () => {
    mediaRecorder.current?.pause()
  }

  const continueRecording = () => {
    mediaRecorder.current?.resume()
  }

  const stopRecordingAndGetFile = (): Promise<File> => {
    return new Promise((resolve, reject) => {
      if (!mediaRecorder.current) {
        return reject(new Error('mediaRecorder is null.'))
      }

      mediaRecorder.current.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: mimeType })
        const file = new File([audioBlob], `user-audio-${Date.now()}.webm`, {
          type: mimeType,
        })

        setFile(file)
        setAudioChunks([])
        resolve(file)
      }

      mediaRecorder.current.stop()
    })
  }

  return {
    initializeRoomRecorder,
    initializeAudioRecorder,
    continueRecording,
    pauseRecording,
    stopRecordingAndGetFile,
    file,
  }
}
